import { OrderSide, OrderSideChar } from '../index';

export enum TransactionTypeCode {
    order = 79,
    send = 83,
    receive = 86,
    withdrawal = 87,
    deposit = 68,
    interest = 73,
    new_pledge = 80,
    pledge_add_on = 65,
    reclaim = 66,
    partial_reclaim = 67,
    release = 69,
    partial_release = 70
}

export enum TransactionTypeChar {
    send = 's',
    receive = 'r',
    deposit = 'd',
    transfer = 't',
    withdrawal = 'w',
    order = 'o',
    int_distribution = 'i',
    new_pledge = 'P',
    pledge_add_on = 'A',
    reclaim = 'B',
    partial_reclaim = 'C',
    release = 'E',
    partial_release = 'F'
}

export enum TransactionTypeString {
    send = 'Send',
    receive = 'Receive',
    deposit = 'Deposit',
    transfer = 'Transfer',
    ach = 'ACH',
    fedwire = 'Fedwire',
    withdrawal = 'Withdrawal',
    order = 'Order',
    int_distribution = 'Interest',
    new_pledge = 'New Post',
    pledge_add_on = 'Post Add On',
    reclaim = 'Reclaim',
    partial_reclaim = 'Partial Reclaim',
    release = 'Release',
    partial_release = 'Partial Release',
    unk = 'UNK'
}

export enum TransactionTypeEnum {
    deposit = 'deposit',
    send = 'send',
    transfer = 'transfer',
    adjustment = 'adjustment',
    ach = 'ach',
    fedwire = 'fedwire',
    withdrawal = 'withdrawal',
    order = 'order',
    int_distribution = 'int_distribution',
    new_pledge = 'new_pledge',
    pledge_add_on = 'pledge_add_on',
    reclaim = 'reclaim',
    partial_reclaim = 'partial_reclaim',
    release = 'release',
    partial_release = 'partial_release'
}

export enum PledgeTransactionTypeCode {
    new_pledge = TransactionTypeCode.new_pledge,
    pledge_add_on = TransactionTypeCode.pledge_add_on,
    reclaim = TransactionTypeCode.reclaim,
    partial_reclaim = TransactionTypeCode.partial_reclaim,
    release = TransactionTypeCode.release,
    partial_release = TransactionTypeCode.partial_release
}

export enum PledgeTransactionTypeChar {
    new_pledge = TransactionTypeChar.new_pledge,
    pledge_add_on = TransactionTypeChar.pledge_add_on,
    reclaim = TransactionTypeChar.reclaim,
    partial_reclaim = TransactionTypeChar.partial_reclaim,
    release = TransactionTypeChar.release,
    partial_release = TransactionTypeChar.partial_release
}

export enum PledgeTransactionTypeString {
    new_pledge = TransactionTypeString.new_pledge,
    pledge_add_on = TransactionTypeString.pledge_add_on,
    reclaim = TransactionTypeString.reclaim,
    partial_reclaim = TransactionTypeString.partial_reclaim,
    release = TransactionTypeString.release,
    partial_release = TransactionTypeString.partial_release
}

export enum PledgeTransactionTypeEnum {
    new_pledge = TransactionTypeEnum.new_pledge,
    pledge_add_on = TransactionTypeEnum.pledge_add_on,
    reclaim = TransactionTypeEnum.reclaim,
    partial_reclaim = TransactionTypeEnum.partial_reclaim,
    release = TransactionTypeEnum.release,
    partial_release = TransactionTypeEnum.partial_release
}

export const pledgeTransactionTypeCodeToEnum = (code: number | string): PledgeTransactionTypeEnum | null => {
    switch (Number(code)) {
        case PledgeTransactionTypeCode.new_pledge:
            return PledgeTransactionTypeEnum.new_pledge;
        case PledgeTransactionTypeCode.pledge_add_on:
            return PledgeTransactionTypeEnum.pledge_add_on;
        case PledgeTransactionTypeCode.reclaim:
            return PledgeTransactionTypeEnum.reclaim;
        case PledgeTransactionTypeCode.partial_reclaim:
            return PledgeTransactionTypeEnum.partial_reclaim;
        case PledgeTransactionTypeCode.release:
            return PledgeTransactionTypeEnum.release;
        case PledgeTransactionTypeCode.partial_release:
            return PledgeTransactionTypeEnum.partial_release;
        default:
            return null;
    }
};

/*
Does not include all transaction statuses
Will hold enums for "Actionable" statuses
Such as Maker/Approver approvals
As well as Exception Workflow handling
*/
export enum TransactionStatusCode {
    // Fred statuses - see confluence doc
    REJECTED = 50,
    REQ_PENDING = 49, // 1
    PENDING = 55, // 7
    COMPLETED = 67, // C
    ACKNOWLEDGED = 65, // A
    CANCELED = 78, // N
    FAILED = 54, // 6
    PARTIAL_FILLED = 80, // P
    EXPIRED = 69, //E
    DECLINED = 88, // X
    APPROVED = 89, // Y
    ID_OPEN = 79, // O
    PROCESSING = 71, // G
    NEW = 110, // n
    ACTIVE = 104, // h

    // TODO Check PENDING_APPROVAL final value when it is ready
    PENDING_APPROVAL = 84, // T => Fred value
    // PENDING_APPROVAL = 86, // V

    BANK_NO_ACK = 102, // f

    // IBN EXCEPTION WORKFLOW
    // The below are the CORRECT values to check on
    BANK_REJ = 53, // 5
    RETRY_BANK_REJ = 113 // q
}

type TransactionStatusCodeMap = Partial<Record<
    TransactionStatusCode,
    { displayValue: string; value: string }
>>;

export const TransactionStatusCodeMap: TransactionStatusCodeMap = {
    [TransactionStatusCode.REJECTED]: {
        displayValue: 'Rejected',
        value: '2'
    },
    [TransactionStatusCode.PENDING_APPROVAL]: {
        displayValue: 'Pending Approval',
        value: 'T'
    },
    [TransactionStatusCode.PENDING]: {
        displayValue: 'Pending',
        value: '7'
    },
    [TransactionStatusCode.PROCESSING]: {
        displayValue: 'Processing',
        value: 'G'
    },
    [TransactionStatusCode.FAILED]: {
        displayValue: 'Failed',
        value: '6'
    },
    [TransactionStatusCode.CANCELED]: {
        displayValue: 'Canceled',
        value: 'N'
    },
    [TransactionStatusCode.NEW]: {
        displayValue: 'New',
        value: 'n'
    },
    [TransactionStatusCode.ACTIVE]: {
        displayValue: 'Active',
        value: 'h'
    },
    [TransactionStatusCode.DECLINED]: {
        displayValue: 'Declined',
        value: 'X'
    },
    [TransactionStatusCode.COMPLETED]: {
        displayValue: 'Done',
        value: 'C'
    },
    [TransactionStatusCode.ACKNOWLEDGED]: {
        displayValue: 'Acknowledged',
        value: 'A'
    },
    [TransactionStatusCode.EXPIRED]: {
        displayValue: 'Expired',
        value: 'E'
    }
};

export enum TransactionDirectionCode {
    in = 50,
    out = 49,
    unk = -1
}

export enum TransactionDirectionString {
    in = 'IN',
    out = 'OUT',
    unk = 'UNK'
}

export enum FailedTransactionWorkflowCodes {
    APPROVE = 65, // A
    RETRY = 66, // B
    DECLINE = 82 // R
}

export enum RejectCode {
    invalid_credentials = 67,
    invalid_account = 88,
    in_maintenance_window = 87,
    internal_server_error = 69,
    bad_request = 66,
    bad_json = 70,
    no_ack_received = 78,
    needs_attention = 65,
    empty_content = 82,
    no_connection = 83,
    bad_response = 84
}

export enum RejectCodeString {
    invalid_credentials = 'Invalid Credentials',
    invalid_account = 'Invalid Account',
    in_maintenance_window = 'In Maintenance Window',
    internal_server_error = 'Internal Server Error',
    bad_request = 'Bad Request',
    bad_json = 'Bad JSON',
    no_ack_received = 'No ACK Received',
    needs_attention = 'Needs Attention',
    empty_content = 'Empty Content',
    no_connection = 'No Connection',
    bad_response = 'Bad Response',
    none = 'None'
}

export const transactionTypeMap = (type: TransactionTypeCode) => {
    switch (type) {
        case TransactionTypeCode.send:
            return TransactionTypeString.send;
        case TransactionTypeCode.receive:
            return TransactionTypeString.receive;
        case TransactionTypeCode.deposit:
            return TransactionTypeString.deposit;
        case TransactionTypeCode.order:
            return TransactionTypeString.order;
        case TransactionTypeCode.withdrawal:
            return TransactionTypeString.withdrawal;
        case TransactionTypeCode.interest:
            return TransactionTypeString.int_distribution;
        case TransactionTypeCode.new_pledge:
            return TransactionTypeString.new_pledge;
        case TransactionTypeCode.pledge_add_on:
            return TransactionTypeString.pledge_add_on;
        case TransactionTypeCode.reclaim:
            return TransactionTypeString.reclaim;
        case TransactionTypeCode.partial_reclaim:
            return TransactionTypeString.partial_reclaim;
        case TransactionTypeCode.release:
            return TransactionTypeString.release;
        case TransactionTypeCode.partial_release:
            return TransactionTypeString.partial_release;
        default:
            return TransactionTypeString.unk;
    }
};

export const transactionTypeCharMap = (type: TransactionTypeChar | string) => {
    switch (type) {
        case TransactionTypeChar.send:
            return TransactionTypeString.send;
        case TransactionTypeChar.receive:
            return TransactionTypeString.receive;
        case TransactionTypeChar.deposit:
            return TransactionTypeString.deposit;
        case TransactionTypeChar.transfer:
            return TransactionTypeString.transfer;
        case TransactionTypeChar.order:
            return TransactionTypeString.order;
        case TransactionTypeChar.withdrawal:
            return TransactionTypeString.withdrawal;
        case TransactionTypeChar.int_distribution:
            return TransactionTypeString.int_distribution;
        case TransactionTypeChar.new_pledge:
            return TransactionTypeString.new_pledge;
        case TransactionTypeChar.pledge_add_on:
            return TransactionTypeString.pledge_add_on;
        case TransactionTypeChar.reclaim:
            return TransactionTypeString.reclaim;
        case TransactionTypeChar.partial_reclaim:
            return TransactionTypeString.partial_reclaim;
        case TransactionTypeChar.release:
            return TransactionTypeString.release;
        case TransactionTypeChar.partial_release:
            return TransactionTypeString.partial_release;
        default:
            return TransactionTypeString.unk;
    }
};

export const orderSideTypeCharMap = (type: OrderSide | string) => {
    switch (type) {
        case OrderSide.BUY:
            return OrderSideChar.B;
        case OrderSide.SELL:
            return OrderSideChar.S;
        case OrderSide.SUBSCRIPTION:
            return OrderSideChar.U;
        case OrderSide.REDEEM:
            return OrderSideChar.R;
    }
};

export const transactionDirectionnMap = (direction: TransactionDirectionCode) => {
    switch (direction) {
        case TransactionDirectionCode.in:
            return TransactionDirectionCode.in;
        case TransactionDirectionCode.out:
            return TransactionDirectionCode.out;
        default:
            return TransactionDirectionCode.unk;
    }
};

export const rejectCodeMap = (code: RejectCode) => {
    switch (code) {
        case RejectCode.invalid_credentials:
            return RejectCodeString.invalid_credentials;
        case RejectCode.invalid_account:
            return RejectCodeString.invalid_account;
        case RejectCode.in_maintenance_window:
            return RejectCodeString.in_maintenance_window;
        case RejectCode.internal_server_error:
            return RejectCodeString.internal_server_error;
        case RejectCode.bad_request:
            return RejectCodeString.bad_request;
        case RejectCode.bad_json:
            return RejectCodeString.bad_json;
        case RejectCode.no_ack_received:
            return RejectCodeString.no_ack_received;
        case RejectCode.needs_attention:
            return RejectCodeString.needs_attention;
        case RejectCode.empty_content:
            return RejectCodeString.empty_content;
        case RejectCode.no_connection:
            return RejectCodeString.no_connection;
        case RejectCode.bad_response:
            return RejectCodeString.no_connection;
        default:
            return RejectCodeString.none;
    }
};

export enum SendAssetPurposeMappedEnum {
    COLLATERAL_RELEASE = 'COLLATERAL_RELEASE',
    INTERCOMPANY_TRANSFER = 'INTERCOMPANY_TRANSFER',
    SETTLEMENT_OF_OFF_PLATFORM_DIGITAL_ASSET_TRANSACTION = 'SETTLEMENT_OF_OFF_PLATFORM_DIGITAL_ASSET_TRANSACTION',
    INVESTMENT_PURPOSES = 'INVESTMENT_PURPOSES',
    PAYMENT_FOR_GOODS_SERVICES_OR_OTHER_OBLIGATIONS = 'PAYMENT_FOR_GOODS_SERVICES_OR_OTHER_OBLIGATIONS',
    OTHER = 'OTHER'
}

export enum AssetPurposeTypeChar {
    collateral_release = 'C',
    intercompany_transfer = 'T',
    settlement_of_off_platform_digital_asset_transaction = 'S',
    investment_purposes = 'I',
    payment_for_goods_services_or_other_obligations = 'P',
    other = 'O'
}

export enum AssetPurposeTypeString {
    collateral_release = 'Collateral Release',
    intercompany_transfer = 'Intercompany Transfer',
    settlement_of_off_platform_digital_asset_transaction = 'Settlement of Off-Platform Digital Asset Transaction',
    investment_purposes = 'Investment Purposes',
    payment_for_goods_services_or_other_obligations = 'Payment for Goods, Services or other Obligations',
    other = 'Other',
    unk = 'Unknown'
}

export const assetPurposeTypeCharMap = (type: TransactionTypeChar | string) => {
    switch (type) {
        case AssetPurposeTypeChar.collateral_release:
            return AssetPurposeTypeString.collateral_release;
        case AssetPurposeTypeChar.intercompany_transfer:
            return AssetPurposeTypeString.intercompany_transfer;
        case AssetPurposeTypeChar.settlement_of_off_platform_digital_asset_transaction:
            return AssetPurposeTypeString.settlement_of_off_platform_digital_asset_transaction;
        case AssetPurposeTypeChar.investment_purposes:
            return AssetPurposeTypeString.investment_purposes;
        case AssetPurposeTypeChar.payment_for_goods_services_or_other_obligations:
            return AssetPurposeTypeString.payment_for_goods_services_or_other_obligations;
        case AssetPurposeTypeChar.other:
            return AssetPurposeTypeString.other;
        default:
            return AssetPurposeTypeString.unk;
    }
};